import { ReactNode, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

export type StatusTabState = "complete" | "incomplete" | "muted";

export type StatusTabItem = {
  key: string;
  title: string;
  status: StatusTabState;
  children: ReactNode;
};

const statusStyles: Record<StatusTabState, { active: string; inactive: string; indicator: string }> = {
  complete: {
    active: "bg-white text-green-700 border-green-200 shadow-sm",
    inactive: "bg-transparent text-green-600 border-transparent hover:bg-green-50",
    indicator: "bg-green-500",
  },
  incomplete: {
    active: "bg-white text-amber-700 border-amber-300 shadow-sm",
    inactive: "bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100",
    indicator: "bg-amber-400",
  },
  muted: {
    active: "bg-white text-gray-500 border-gray-200 shadow-sm",
    inactive: "bg-transparent text-gray-400 border-transparent hover:bg-gray-50",
    indicator: "bg-gray-300",
  },
};

export default function StatusTabs({
  tabs,
  defaultTabKey,
  onTabChange,
  keepMounted = false,
  showActiveTitle = false,
}: {
  tabs: StatusTabItem[];
  defaultTabKey?: string;
  onTabChange?: (tabKey: string) => void;
  /** Keep all panels mounted (hiding inactive ones) so their state survives tab switches. */
  keepMounted?: boolean;
  /** Render the active tab's title as a header above the panel content. */
  showActiveTitle?: boolean;
}) {
  const { t } = useTranslation("common");
  const fallbackTabKey = useMemo(() => tabs[0]?.key ?? "", [tabs]);
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey ?? fallbackTabKey);

  const activeTab = tabs.find((tab) => tab.key === activeTabKey) ?? tabs[0];

  if (!activeTab) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50">
        <nav className="flex gap-1 px-3 pt-2" aria-label={t("Lead details tabs")}>
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab.key;
            const styles = statusStyles[tab.status];

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTabKey(tab.key);
                  onTabChange?.(tab.key);
                }}
                className={classNames(
                  "whitespace-nowrap rounded-t-md border border-b-0 py-2 px-4 text-sm font-medium transition-all -mb-px flex items-center gap-2",
                  isActive ? styles.active : styles.inactive
                )}
              >
                <span>{tab.title}</span>
                <span className={classNames("h-2 w-2 rounded-full shrink-0", styles.indicator)} />
              </button>
            );
          })}
        </nav>
      </div>

      {showActiveTitle && (
        <div className="border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">{activeTab.title}</h3>
        </div>
      )}

      {keepMounted ? (
        tabs.map((tab) => (
          <div key={tab.key} className={tab.key === activeTab.key ? "" : "hidden"}>
            {tab.children}
          </div>
        ))
      ) : (
        <div>{activeTab.children}</div>
      )}
    </div>
  );
}
